/**
 * The "Usuarios OTC" panel — full CRUD for the over-the-counter terminal logins nested under one
 * location. The pin is shown and edited as plain text, matching `packages/contracts/src/otc-users.ts`'s
 * own decision not to hash it: this is a shared till code an operator reads off a screen, not a
 * personal password.
 */
import type { CreateOtcUser, OtcUser, UpdateOtcUser } from '@apuestapr/contracts';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Field,
  Input,
  icons,
  toast,
} from '@apuestapr/ui';
import { useState, type FormEvent, type ReactElement } from 'react';

import type { ManagedRowList } from './hooks.ts';

interface Draft {
  readonly username: string;
  readonly pin: string;
}

const emptyDraft: Draft = { username: '', pin: '' };

interface OtcUserRowProps {
  readonly otcUser: OtcUser;
  readonly busy: boolean;
  readonly onUpdate: (id: number, patch: Partial<Draft>) => void;
  readonly onDelete: (id: number) => void;
}

const OtcUserRow = ({ otcUser, busy, onUpdate, onDelete }: OtcUserRowProps): ReactElement => {
  const [username, setUsername] = useState(otcUser.username);
  const [pin, setPin] = useState(otcUser.pin);

  return (
    <li className="grid gap-3 rounded-md border border-border p-3 sm:grid-cols-[1fr_1fr_auto]">
      <Field label="Usuario" controlId={`otc-${String(otcUser.id)}-username`}>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={username}
            onChange={(event) => setUsername(event.currentTarget.value)}
            onBlur={() => onUpdate(otcUser.id, { username })}
          />
        )}
      </Field>
      <Field label="PIN" controlId={`otc-${String(otcUser.id)}-pin`}>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            maxLength={10}
            value={pin}
            onChange={(event) => setPin(event.currentTarget.value)}
            onBlur={() => onUpdate(otcUser.id, { pin })}
          />
        )}
      </Field>
      <div className="flex items-end">
        <Button
          disabled={busy}
          onClick={() => onDelete(otcUser.id)}
          size="sm"
          variant="destructive"
        >
          Eliminar <span className="sr-only">{otcUser.username}</span>
        </Button>
      </div>
      <p className="col-span-full m-0 text-sm text-muted-foreground">Rol: {otcUser.role}</p>
    </li>
  );
};

const CreateOtcUserForm = ({
  busy,
  onCreate,
}: {
  readonly busy: boolean;
  readonly onCreate: (draft: Draft) => void;
}): ReactElement => {
  const [draft, setDraft] = useState<Draft>(emptyDraft);

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (draft.username.trim() === '' || draft.pin.trim() === '') return;
    onCreate(draft);
    setDraft(emptyDraft);
  };

  return (
    <form
      className="grid gap-3 rounded-md border border-dashed border-border p-3 sm:grid-cols-3"
      noValidate
      onSubmit={submit}
    >
      <Field label="Usuario del nuevo acceso" controlId="new-otc-username" required>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            value={draft.username}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, username: value }));
            }}
          />
        )}
      </Field>
      <Field label="PIN del nuevo acceso" controlId="new-otc-pin" required>
        {(control) => (
          <Input
            {...control}
            disabled={busy}
            maxLength={10}
            value={draft.pin}
            onChange={(event) => {
              const { value } = event.currentTarget;
              setDraft((current) => ({ ...current, pin: value }));
            }}
          />
        )}
      </Field>
      <div className="flex items-end">
        <Button disabled={busy} type="submit">
          Añadir usuario OTC
        </Button>
      </div>
    </form>
  );
};

export interface OtcUsersPanelProps {
  readonly managed: ManagedRowList<OtcUser, CreateOtcUser, UpdateOtcUser>;
}

export const OtcUsersPanel = ({ managed }: OtcUsersPanelProps): ReactElement => {
  const busy = managed.mutation.status === 'loading';

  return (
    <Card>
      <CardHeader>
        <CardTitle level={3}>Usuarios OTC</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {managed.state.status === 'loading' ? (
          <p role="status">Cargando usuarios OTC…</p>
        ) : managed.state.status === 'error' ? (
          <p role="alert">No se pudieron cargar los usuarios OTC.</p>
        ) : managed.state.data.length === 0 ? (
          <EmptyState
            icon={icons.userRound}
            title="Sin usuarios OTC"
            description="Añade el primer usuario de caja de esta localidad."
          />
        ) : (
          <ul className="m-0 grid list-none gap-3 p-0">
            {managed.state.data.map((otcUser) => (
              <OtcUserRow
                key={otcUser.id}
                otcUser={otcUser}
                busy={busy}
                onUpdate={(id, patch) => {
                  void managed.update(id, patch).then((result) => {
                    if (result.ok) toast({ title: 'Usuario OTC actualizado' });
                  });
                }}
                onDelete={(id) => {
                  void managed.remove(id).then((result) => {
                    if (result.ok)
                      toast({ title: 'Usuario OTC eliminado', variant: 'destructive' });
                  });
                }}
              />
            ))}
          </ul>
        )}

        <CreateOtcUserForm
          busy={busy}
          onCreate={(draft) => {
            void managed.create(draft).then((result) => {
              if (result.ok) toast({ title: 'Usuario OTC creado' });
            });
          }}
        />
      </CardContent>
    </Card>
  );
};
